import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Clock, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PopularDestinations = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const destinations = [
    {
      id: 1,
      popular: true,
      flag: '🇺🇸',
      country: t('unitedStates'),
      visaType: t('usaVisitor'),
      description: t('usaDescription'),
      processTime: t('businessDays14'),
      validity: t('upTo12Months'),
      route: '/services/usa-urgent-visa'
    },
    {
      id: 2,
      popular: true,
      flag: '🇬🇧',
      country: t('unitedKingdom'),
      visaType: t('ukVisitor'),
      description: t('ukDescription'),
      processTime: t('businessDays14'),
      validity: t('upTo12Months'),
      route: '/services/urgent-uk-visa'
    },
    {
      id: 3,
      popular: true,
      flag: '🇫🇷',
      country: t('france'),
      visaType: t('franceVisitor'),
      description: t('franceDescription'),
      processTime: t('businessDays14'),
      validity: t('upTo12Months'),
      route: '/services/urgent-schengen-visa'
    },
    {
      id: 4,
      flag: '🇬🇷',
      country: t('greece'),
      visaType: t('greeceVisitor'),
      description: t('greeceDescription'),
      processTime: t('businessDays3'),
      validity: t('upTo12Months'),
      route: '/services/urgent-schengen-visa'
    },
    {
      id: 5,
      flag: '🇪🇸',
      country: t('spain'),
      visaType: t('spainVisitor'),
      description: t('spainDescription'),
      processTime: t('businessDays7'),
      validity: t('upTo12Months'),
      route: '/services/spanish-visa'
    },
    {
      id: 6,
      flag: '🇮🇹',
      country: t('italy'),
      visaType: t('italyVisitor'),
      description: t('italyDescription'),
      processTime: t('businessDays14'),
      validity: t('upTo12Months'),
      route: '/services/italian-visa'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('popularDestinations')}
          </h2>
          <p className="text-xl text-gray-600 mb-2 font-semibold">
            {t('travelTo50Countries')}
          </p>
          <p className="text-base text-gray-600 max-w-3xl mx-auto">
            {t('popularDestinationsDescription')}
          </p>
        </div>

        {/* Destinations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {destinations.map((dest) => (
            <div
              key={dest.id}
              className="bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 group hover:border-teal-200"
            >
              {/* Popular Badge */}
              {dest.popular && (
                <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold px-4 py-1 inline-block">
                  {t('popular')}
                </div>
              )}

              {/* Card Content */}
              <div className="p-6">
                {/* Country Header */}
                <div className="flex items-start space-x-3 mb-4">
                  <span className="text-5xl">{dest.flag}</span>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">
                      {dest.country}
                    </h3>
                    <p className="text-sm font-semibold text-logo-blue">
                      {dest.visaType}
                    </p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  {dest.description}
                </p>

                {/* Info Badges */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Calendar className="w-4 h-4 text-logo-blue" />
                      <span className="font-medium">{dest.validity}</span>
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-teal-100 rounded-lg px-3 py-2">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-logo-blue" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-600 font-medium">{t('processIn')}</p>
                        <p className="text-sm font-bold text-blue-700">{dest.processTime}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col space-y-2">
                  <button
                    onClick={() => navigate(`/apply?destination=${encodeURIComponent(dest.country)}&type=${encodeURIComponent(dest.visaType)}`)}
                    className="w-full bg-gradient-to-r from-logo-blue to-logo-cyan text-white px-4 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all shadow-md hover:shadow-lg transform hover:scale-105 flex items-center justify-center space-x-2"
                  >
                    <span>{t('applyNow')}</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => navigate(dest.route)}
                    className="w-full bg-white text-logo-blue border-2 border-logo-blue px-4 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-all"
                  >
                    {t('viewDetails')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <button
            onClick={() => navigate('/apply')}
            className="inline-flex items-center space-x-2 bg-gray-900 text-white px-8 py-4 rounded-full font-semibold hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <span>{t('viewAllDestinations')}</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default PopularDestinations;
